import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../http/api.service';

export interface ThemeOption {
  Id: string;
  Name: string;
  PrimaryColor: string;
  SecondaryColor: string | null;
  IsActive: boolean;
}

export interface ThemeOptionInput {
  Name: string;
  PrimaryColor: string;
  SecondaryColor?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class EnvironmentThemeService {
  constructor(private _http: HttpClient, private api: ApiService) { }

  private sanitizeTheme(theme: ThemeOption): ThemeOption {
    if (!theme) return theme;
    let primary = theme.PrimaryColor || '#039be5';
    let secondary = theme.SecondaryColor;

    if (primary.toUpperCase() === '#039BE5') {
      if (!secondary || secondary.toUpperCase() === '#309ACF') {
        secondary = '#5bb6e4';
      }
    } else if (primary.toUpperCase() === '#FE8C00' && secondary?.toUpperCase() === '#E45832') {
      // In the database 'Naranja' has colors inverted from the UI design specification
      primary = '#e45832';
      secondary = '#fe8c00';
    }

    return {
      ...theme,
      PrimaryColor: primary,
      SecondaryColor: secondary
    };
  }

  getActiveTheme(): Observable<ThemeOption> {
    return this._http.get<ThemeOption>(this.api.getUrl('api/EnvironmentTheme')).pipe(
      map(theme => this.sanitizeTheme(theme))
    );
  }

  getOptions(): Observable<ThemeOption[]> {
    return this._http.get<ThemeOption[]>(this.api.getUrl('api/EnvironmentTheme/Options')).pipe(
      map(options => Array.isArray(options) ? options.map(opt => this.sanitizeTheme(opt)) : options)
    );
  }

  createOption(input: ThemeOptionInput): Observable<ThemeOption> {
    return this._http.post<ThemeOption>(this.api.getUrl('api/EnvironmentTheme/Options'), input);
  }

  updateOption(id: string, input: ThemeOptionInput): Observable<ThemeOption> {
    return this._http.put<ThemeOption>(this.api.getUrl(`api/EnvironmentTheme/Options/${id}`), input);
  }

  activateOption(id: string): Observable<ThemeOption> {
    return this._http.put<ThemeOption>(this.api.getUrl(`api/EnvironmentTheme/Options/${id}/Activate`), {});
  }

  deleteOption(id: string): Observable<ThemeOption> {
    return this._http.delete<ThemeOption>(this.api.getUrl(`api/EnvironmentTheme/Options/${id}`));
  }
}
